/*
 * Description:
 * ---------------------
 * Where everything sits on the master map, worked out from counts alone so
 * the map never measures itself. A box linked with three or more others is a
 * hub: it stands in the middle and the rest on either side of it, boxes linked
 * other than through it kept together, next to each other, on one side. With
 * no hub, boxes stand in columns so that most access runs left to right (a
 * loop breaks where fewer rules cross). Boxes with no access stand apart in a
 * column of their own, and a column of very many wraps into several. Every
 * column is centred as if every box were folded, so opening a box moves only
 * the boxes under it in its column. Lines run between ports, a stub out of a
 * box's side ending in a circle: every box has its own, beside its name,
 * standing for all its devices, and an open box one per device. A line leaves
 * its box's own port on the side facing the other end, or for two boxes in one
 * column on their outer side, and ends at the port of the device it reaches,
 * or of the box while that is folded; lines meeting at a port fan out from it
 * in the order of where they go, so they never cross there. A label sits on
 * its line near the box it points into, where it covers no box, port or other
 * label.
 *
 * Box panels open against the map's right side, each in the first place there
 * no other docked panel holds, the row stepping down once full. A panel keeps
 * its place until it is dragged, then stays where it was put: one panel moving
 * never moves another. The map fits beside the docked panels.
 *
 * ! file components/mapLayout.js
 */

export const BOX_W = 288;
export const HEAD_H = 92;
export const ROW_H = 44;
export const NOTE_H = 34;
export const MARGIN = 40;
const FOOT_H = 10;
// An open box's rows begin under its head (HEAD_H inside its borders).
const ROWS_TOP = HEAD_H - 1;
const GAP_X = 220;
const GAP_WRAP = 72;
const GAP_Y = 26;
const WRAP = 12;
// A box linked with at least HUB others stands in the middle.
const HUB = 3;
const LABEL_H = 22;
// A port: a stub of STUB out of a box's side, ending in a circle of BOX_R (the box's own, PORT_Y down, by its name) or DEVICE_R.
const STUB = 24;
const PORT_Y = 26;
export const BOX_R = 6.5;
export const DEVICE_R = 4.5;
// Lines meeting at one port leave it FAN apart, FAN_SPAN at most in all; lines between the same two ports bow BOW apart.
const FAN = 18;
const FAN_SPAN = 120;
const BOW = 36;
// How far a line between two boxes of one column bulges out of it, and the room it and its label need beyond that.
const ARC = 56;
const ARC_ROOM = STUB + ARC + 48;
// Where along its line a label may sit, first choice first: by the box the line points into.
const NEAR_END = [0.74, 0.64, 0.82, 0.54, 0.44, 0.34];

/* The height of an open box: its head, its device rows and the notes under
   them. A folded box is HEAD_H. */
export const boxHeight = (rows, notes) => Math.max(HEAD_H, ROWS_TOP + rows * ROW_H + notes * NOTE_H + FOOT_H);

/* Where, within an open box, each device row's middle sits. */
export const rowY = (i) => ROWS_TOP + i * ROW_H + ROW_H / 2;

/* columns sets the boxes in columns, as [{layer, ids}]: layer orders them
   left to right, below 0 left of the middle. pairs are [{from, to, n}], one
   per source box and owner box with the number of rules between them. */
export function columns(ids, pairs) {
  const linked = new Set(pairs.flatMap((p) => [p.from, p.to]));
  const alone = ids.filter((id) => !linked.has(id)).sort((a, b) => a.localeCompare(b));
  if (!linked.size) {
    const across = Math.min(alone.length, 3);
    return Array.from({ length: across }, (_, i) => ({ layer: i, ids: alone.filter((_, j) => j % across === i) }));
  }
  const near = new Map([...linked].sort((a, b) => a.localeCompare(b)).map((id) => [id, new Set()]));
  for (const p of pairs) {
    near.get(p.from).add(p.to);
    near.get(p.to).add(p.from);
  }
  const hub = [...near.keys()].sort((a, b) => near.get(b).size - near.get(a).size)[0];
  const cols = near.get(hub).size >= HUB ? around(hub, near) : layered([...near.keys()], pairs);
  if (alone.length) cols.push({ layer: Math.max(...cols.map((c) => c.layer)) + 1, ids: alone });
  // A column of more than WRAP boxes wraps into several, outwards from the middle.
  return cols.flatMap((c) => {
    const parts = Math.ceil(c.ids.length / WRAP);
    const size = Math.ceil(c.ids.length / parts);
    const split = Array.from({ length: parts }, (_, j) => ({ layer: c.layer, ids: c.ids.slice(j * size, (j + 1) * size) }));
    return c.layer < 0 ? split.reverse() : split;
  });
}

/* around stands a hub in the middle and the rest on its two sides, about as
   many on each: each group of boxes linked other than through the hub stays
   together, the most linked of it first and the rest in the order they link. */
function around(hub, near) {
  const seen = new Set([hub]);
  const inside = (id, group) => [...near.get(id)].filter((n) => group.includes(n)).length;
  const groups = [];
  for (const id of near.keys()) {
    if (seen.has(id)) continue;
    const group = [id];
    seen.add(id);
    for (let i = 0; i < group.length; i++) {
      for (const n of near.get(group[i])) {
        if (!seen.has(n)) {
          seen.add(n);
          group.push(n);
        }
      }
    }
    const first = [...group].sort((a, b) => inside(b, group) - inside(a, group))[0];
    const order = [first];
    for (let i = 0; i < order.length; i++) {
      for (const n of near.get(order[i])) if (group.includes(n) && !order.includes(n)) order.push(n);
    }
    groups.push(order);
  }
  groups.sort((a, b) => b.length - a.length);
  const total = groups.reduce((n, g) => n + g.length, 0);
  const left = [];
  const right = [];
  for (const g of groups) (left.length * 2 < total ? left : right).push(...g);
  return [{ layer: -1, ids: left }, { layer: 0, ids: [hub] }, { layer: 1, ids: right }].filter((c) => c.ids.length);
}

/* layered stands the boxes in columns so that most access runs left to right:
   a box whose access runs to others sits left of them where it can, a loop
   broken where fewer rules cross, and each column after the first follows the
   order of its neighbours to its left. */
function layered(linked, pairs) {
  const net = (id) => pairs.reduce((s, p) => s + (p.from === id ? p.n : 0) - (p.to === id ? p.n : 0), 0);
  const order = [...linked].sort((a, b) => net(b) - net(a) || a.localeCompare(b));
  const rank = Object.fromEntries(order.map((id, i) => [id, i]));
  const layer = {};
  for (const id of order) {
    layer[id] = Math.max(0, ...pairs.filter((p) => p.to === id && rank[p.from] < rank[id]).map((p) => layer[p.from] + 1));
  }
  const layers = [];
  for (const id of order) (layers[layer[id]] ||= []).push(id);
  for (let i = 1; i < layers.length; i++) {
    const left = Object.fromEntries(layers[i - 1].map((id, j) => [id, j]));
    const near = (id) => pairs.flatMap((p) => (p.from === id ? [p.to] : p.to === id ? [p.from] : [])).filter((o) => o in left);
    const centre = (id) => {
      const n = near(id);
      return n.length ? n.reduce((s, o) => s + left[o], 0) / n.length : Infinity;
    };
    layers[i].sort((a, b) => centre(a) - centre(b) || a.localeCompare(b));
  }
  return layers.map((l, i) => ({ layer: i, ids: l }));
}

/* place sets each box in its column. heights maps an open box to its height;
   a box not in it is folded. Answers {pos: {id: {x, y, w, h, out}}, w, h},
   out the side facing away from the middle. */
export function place(cols, heights = {}) {
  const span = (c) => c.ids.length * HEAD_H + (c.ids.length - 1) * GAP_Y;
  const tallest = Math.max(0, ...cols.map(span));
  const pos = {};
  let x = MARGIN;
  cols.forEach((c, i) => {
    if (i) x += BOX_W + (cols[i - 1].layer === c.layer ? GAP_WRAP : GAP_X);
    let y = MARGIN + (tallest - span(c)) / 2;
    for (const id of c.ids) {
      const h = heights[id] || HEAD_H;
      pos[id] = { x, y, w: BOX_W, h, out: c.layer < 0 ? "left" : "right" };
      y += h + GAP_Y;
    }
  });
  const boxes = Object.values(pos);
  return { pos, w: (boxes.length ? x + BOX_W : 0) + MARGIN, h: Math.max(0, ...boxes.map((p) => p.y + p.h)) + MARGIN };
}

/* connect draws the access between ports. A port is a stub out of a box's
   side ending in a circle: the box's own, beside its name, standing for every
   device in it, and in an open box one per device row. A line {key, from, to,
   row, label} runs from the from box's own port, on the side facing the other
   end (for two boxes in one column, their outer side), to the port of the
   device it reaches, row its index in the open owner box, else to the owner
   box's own, and ends in an arrow at that port. Lines meeting at a port fan
   out from it in the order of where they go, so none cross there, and two
   between the same ports bow apart. Every box has its own port and an open box
   one per row, on the side its lines use, else facing the middle. open maps
   an open box to its number of rows. Answers {paths: {key: {d, label, ends}},
   ports: [{key, box, row, side, x0, x, y, used}]}: label as {x, y, w, text},
   ends the keys of the line's two ports, row null for a box's own port. */
export function connect(lines, pos, open = {}) {
  const ports = new Map();
  const port = (box, side, row) => {
    const key = `${box}|${side}|${row ?? "box"}`;
    if (!ports.has(key)) {
      const p = pos[box];
      const x0 = side === "right" ? p.x + p.w : p.x;
      ports.set(key, { key, box, row, side, x0, x: x0 + (side === "right" ? STUB : -STUB), y: p.y + (row == null ? PORT_Y : rowY(row)), lines: [] });
    }
    return ports.get(key);
  };
  const drawn = lines.map((l, i) => {
    const a = pos[l.from];
    const b = pos[l.to];
    const [out, into] = a.x < b.x ? ["right", "left"] : a.x > b.x ? ["left", "right"] : [a.out, a.out];
    const d = { l, out, into, s: port(l.from, out, null), e: port(l.to, into, l.row), bend: { s: 0, e: 0 } };
    d.s.lines.push({ d, end: "s", other: d.e.y, i });
    d.e.lines.push({ d, end: "e", other: d.s.y, i });
    return d;
  });
  for (const p of ports.values()) {
    p.lines.sort((m, n) => m.other - n.other || m.i - n.i);
    const step = p.lines.length > 1 ? Math.min(FAN, FAN_SPAN / (p.lines.length - 1)) : 0;
    p.lines.forEach((m, k) => {
      m.d.bend[m.end] = (k - (p.lines.length - 1) / 2) * step;
    });
  }
  const twins = new Map();
  for (const d of drawn) {
    const pair = [d.s.key, d.e.key].sort().join(" ");
    if (!twins.has(pair)) twins.set(pair, []);
    twins.get(pair).push(d);
  }
  for (const group of twins.values()) {
    if (group.length < 2) continue;
    group.forEach((d, k) => {
      d.bend.s = (k - (group.length - 1) / 2) * BOW;
      d.bend.e = d.bend.s;
    });
  }
  const centres = Object.values(pos).map((p) => p.x + p.w / 2);
  const middle = (Math.min(...centres) + Math.max(...centres)) / 2;
  for (const [id, p] of Object.entries(pos)) {
    const mine = [...ports.values()].filter((q) => q.box === id);
    const right = mine.filter((q) => q.side === "right").length;
    const side = mine.length ? (right * 2 >= mine.length ? "right" : "left") : (p.x + p.w / 2 <= middle ? "right" : "left");
    if (!mine.some((q) => q.row == null)) port(id, side, null);
    for (let r = 0; r < (open[id] || 0); r++) if (!mine.some((q) => q.row === r)) port(id, side, r);
  }
  const blocks = [
    ...Object.values(pos).map((p) => ({ x: p.x - 2, y: p.y - 2, w: p.w + 4, h: p.h + 4 })),
    ...[...ports.values()].map((p) => ({ x: p.x - 9, y: p.y - 9, w: 18, h: 18 })),
  ];
  const paths = {};
  for (const { l, s, e, out, into, bend } of drawn) {
    const so = out === "right" ? 1 : -1;
    const ei = into === "right" ? 1 : -1;
    // The line stops at the edge of the circle it points into, where its arrow meets it.
    const end = { x: e.x + ei * ((e.row == null ? BOX_R : DEVICE_R) + 1.5), y: e.y };
    const reach = out === into ? ARC : Math.max(40, Math.abs(end.x - s.x) / 2);
    const c0 = { x: s.x + so * reach, y: s.y + bend.s };
    const c1 = { x: end.x + ei * reach, y: end.y + bend.e };
    let label = null;
    if (l.label) {
      const w = labelWidth(l.label);
      const at = (t) => {
        const u = 1 - t;
        return {
          x: u * u * u * s.x + 3 * u * u * t * c0.x + 3 * u * t * t * c1.x + t * t * t * end.x,
          y: u * u * u * s.y + 3 * u * u * t * c0.y + 3 * u * t * t * c1.y + t * t * t * end.y,
        };
      };
      const box = (p) => ({ x: p.x - w / 2, y: p.y - LABEL_H / 2, w, h: LABEL_H });
      // The first place clear of every box, port and label, else the one covering least.
      const covered = (r) => blocks.reduce((sum, b) => sum + overlap(r, b), 0);
      const t = NEAR_END.find((v) => !covered(box(at(v)))) ?? NEAR_END.reduce((best, v) => (covered(box(at(v))) < covered(box(at(best))) ? v : best));
      const p = at(t);
      label = { x: Math.round(p.x), y: Math.round(p.y), w, text: l.label };
      blocks.push(box(p));
    }
    paths[l.key] = { d: `M ${s.x} ${s.y} C ${c0.x} ${c0.y}, ${c1.x} ${c1.y}, ${end.x} ${end.y}`, label, ends: [s.key, e.key] };
  }
  return { paths, ports: [...ports.values()].map(({ lines: met, ...p }) => ({ ...p, used: met.length > 0 })) };
}

/* How much two rectangles share. */
const overlap = (a, b) => Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x))
  * Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

/* margins is the room the drawing needs beyond the boxes, {left, right}: a
   line between two boxes of one column bulges out of it on its outer side,
   with its label. */
export function margins(lines, pos) {
  const sides = lines.filter((l) => pos[l.from].x === pos[l.to].x).map((l) => pos[l.from].out);
  return { left: sides.includes("left") ? ARC_ROOM : 0, right: sides.includes("right") ? ARC_ROOM : 0 };
}

/* The width of a label for its text, at the map's label size. */
export const labelWidth = (text) => Math.round(22 + text.length * 6.9);

/* ── Box panels ─────────────────────────────────────────────────────────────
   A panel is {id, mark, z, slot, x, y}: mark is what it brings forward, z its
   place in the stack, slot its place in the dock, and x, y where it was
   dragged to, absent while it is docked. */

export const PANEL_W = 400;
export const MAX_PANELS = 3;
const PANEL_EDGE = 16;
const PANEL_GAP = 12;
const PANEL_STEP = 28;
const PANEL_KEEP = 160;
// The map fits beside docked panels only where that leaves it at least this wide.
const ROOM_MIN = 560;

/* openPanel opens a box's panel bringing mark forward, in the first slot no
   docked panel holds, or brings an open one to the top with it. Past
   MAX_PANELS the one touched longest ago closes. */
export function openPanel(panels, id, mark = {}) {
  const z = Math.max(0, ...panels.map((p) => p.z)) + 1;
  if (panels.some((p) => p.id === id)) return panels.map((p) => (p.id === id ? { ...p, mark, z } : p));
  const oldest = panels.length < MAX_PANELS ? null : panels.reduce((a, b) => (b.z < a.z ? b : a));
  const kept = panels.filter((p) => p !== oldest);
  const held = new Set(kept.filter((p) => p.x == null).map((p) => p.slot));
  let slot = 0;
  while (held.has(slot)) slot++;
  return [...kept, { id, mark, z, slot }];
}

/* raisePanel brings a panel to the top of the stack. */
export function raisePanel(panels, id) {
  const top = Math.max(0, ...panels.map((p) => p.z));
  return panels.find((p) => p.id === id)?.z === top ? panels : panels.map((p) => (p.id === id ? { ...p, z: top + 1 } : p));
}

/* panelSpots answers where each panel stands in a view vw by vh, {id: {x, y}}:
   docked ones by their slot, from the right, the rest where they were
   dragged, enough of each kept in view to drag it back. */
export function panelSpots(panels, vw, vh) {
  const w = Math.min(PANEL_W, vw - 2 * PANEL_EDGE);
  return Object.fromEntries(panels.map((p) => {
    if (p.x != null) return [p.id, { x: Math.min(p.x, Math.max(0, vw - PANEL_KEEP)), y: Math.min(p.y, Math.max(0, vh - PANEL_KEEP)) }];
    const x = vw - PANEL_EDGE - w - p.slot * (w + PANEL_GAP);
    return [p.id, x >= PANEL_EDGE ? { x, y: PANEL_EDGE }
      : { x: Math.max(PANEL_EDGE, vw - PANEL_EDGE - w - PANEL_STEP * p.slot), y: PANEL_EDGE + PANEL_STEP * p.slot }];
  }));
}

/* roomBeside is the width the map fits into beside the docked panels, slots
   their places: left of the farthest of them where that leaves ROOM_MIN, else
   of fewer, else the whole view. */
export function roomBeside(slots, vw) {
  const w = Math.min(PANEL_W, vw - 2 * PANEL_EDGE);
  for (let n = slots.length ? Math.max(...slots) + 1 : 0; n > 0; n--) {
    const room = vw - PANEL_EDGE - n * (w + PANEL_GAP);
    if (room >= ROOM_MIN) return room;
  }
  return vw;
}
